import { useState } from 'react'
import { toast } from 'sonner'
import {
  apiError,
  useApiKeysQuery,
  useGenerateApiKeyMutation,
  useGenerateSigningKeyMutation,
  useRotateApiKeyMutation,
  useRevokeApiKeyMutation,
  useRevokeSigningKeyMutation,
  useRotateSigningKeyMutation,
  useSigningKeysQuery,
} from '@/store/api'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { CopyField } from '@/components/ui/copy-field'
import { Dialog, DialogContent } from '@/components/ui/dialog'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
} from '@/components/ui/dialog'
import { Table, THead, Th, Tr, Td, SkeletonRows } from '@/components/ui/data-table'
import { fmtDate, hoursUntil } from '@/lib/format'
import type { SigningKeyStatus } from '@shared/types'

/** One-time secret reveal pane (design.md §5.8 — "Store this now"). */
function SecretRevealDialog({
  secret,
  title,
  onClose,
}: {
  secret: string | null
  title: string
  onClose: () => void
}) {
  return (
    <Dialog open={!!secret} onOpenChange={(o) => !o && onClose()}>
      <DialogContent title={title} className="max-w-lg">
        <CopyField value={secret ?? ''} />
        <p className="mt-3 text-xs text-ata-orange">Store this now — it won't be shown again.</p>
        <Button variant="secondary" className="mt-4 w-full" onClick={onClose}>
          Done
        </Button>
      </DialogContent>
    </Dialog>
  )
}

function keyStatusBadge(k: { status: SigningKeyStatus; expiresAt: string | null }) {
  if (k.status === 'active') return <Badge variant="active">Active</Badge>
  if (k.status === 'expiring')
    return <Badge variant="expiring">Expiring{k.expiresAt ? ` · in ${hoursUntil(k.expiresAt)}h` : ''}</Badge>
  return <Badge variant="revoked">Revoked</Badge>
}

export function SigningKeysPanel({ slug }: { slug: string }) {
  const { data, isLoading } = useSigningKeysQuery(slug)
  const [generate, { isLoading: generating }] = useGenerateSigningKeyMutation()
  const [rotate] = useRotateSigningKeyMutation()
  const [revoke] = useRevokeSigningKeyMutation()
  const [secret, setSecret] = useState<string | null>(null)

  const onGenerate = async () => {
    try {
      const res = await generate(slug).unwrap()
      setSecret(res.secret)
    } catch (err) {
      toast.error(apiError(err))
    }
  }
  const onRotate = async (id: string) => {
    try {
      const res = await rotate({ slug, id }).unwrap()
      setSecret(res.secret)
      toast.success('Key rotated — your previous key remains valid for 24 hours, then is revoked automatically.')
    } catch (err) {
      toast.error(apiError(err))
    }
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-base font-semibold">SSO signing keys</h3>
          <p className="text-xs text-muted-foreground">
            HMAC keys for <code className="font-mono">POST /sso/exchange</code>. Rotation keeps the old key valid for
            24 hours; explicit revoke is immediate.
          </p>
        </div>
        <Button size="sm" onClick={onGenerate} loading={generating}>
          Generate key
        </Button>
      </div>
      <Table>
        <THead>
          <Th>Key</Th>
          <Th>Status</Th>
          <Th>Created</Th>
          <Th right>Actions</Th>
        </THead>
        {isLoading ? (
          <SkeletonRows cols={4} rows={2} />
        ) : (
          <tbody>
            {data?.length === 0 && (
              <Tr>
                <Td colSpan={4} className="text-center text-muted-foreground">
                  No signing keys yet. Generate one to enable SSO.
                </Td>
              </Tr>
            )}
            {data?.map((k) => (
              <Tr key={k.id}>
                <Td className="font-mono text-xs">{k.keyPrefix}</Td>
                <Td>{keyStatusBadge(k)}</Td>
                <Td>{fmtDate(k.createdAt)}</Td>
                <Td right>
                  {k.status !== 'revoked' && (
                    <div className="flex justify-end gap-2">
                      {k.status === 'active' && (
                        <Button variant="secondary" size="sm" onClick={() => onRotate(k.id)}>
                          Rotate
                        </Button>
                      )}
                      <RevokeKeyButton
                        label="signing key"
                        onConfirm={async () => {
                          await revoke({ slug, id: k.id }).unwrap()
                          toast.success('Signing key revoked')
                        }}
                      />
                    </div>
                  )}
                </Td>
              </Tr>
            ))}
          </tbody>
        )}
      </Table>
      <SecretRevealDialog secret={secret} title="Signing key generated" onClose={() => setSecret(null)} />
    </div>
  )
}

export function ApiKeysPanel({ slug }: { slug: string }) {
  const { data, isLoading } = useApiKeysQuery(slug)
  const [generate, { isLoading: generating }] = useGenerateApiKeyMutation()
  const [revoke] = useRevokeApiKeyMutation()
  const [rotate] = useRotateApiKeyMutation()
  const [secret, setSecret] = useState<string | null>(null)

  const onGenerate = async () => {
    try {
      const res = await generate(slug).unwrap()
      setSecret(res.key)
    } catch (err) {
      toast.error(apiError(err))
    }
  }

  const onRotate = async (id: string) => {
    try {
      const res = await rotate({ slug, id }).unwrap()
      setSecret(res.key)
    } catch (err) {
      toast.error(apiError(err))
    }
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-base font-semibold">API keys</h3>
          <p className="text-xs text-muted-foreground">
            Authenticate Host Provisioning Layer API requests with the <code className="font-mono">x-api-key</code>{' '}
            header. Rotation keeps the old key working for 24 hours; explicit revoke is immediate.
          </p>
        </div>
        <Button size="sm" onClick={onGenerate} loading={generating}>
          Generate key
        </Button>
      </div>
      <Table>
        <THead>
          <Th>Key</Th>
          <Th>Status</Th>
          <Th>Created</Th>
          <Th right>Actions</Th>
        </THead>
        {isLoading ? (
          <SkeletonRows cols={4} rows={2} />
        ) : (
          <tbody>
            {data?.length === 0 && (
              <Tr>
                <Td colSpan={4} className="text-center text-muted-foreground">
                  No API keys yet.
                </Td>
              </Tr>
            )}
            {data?.map((k) => (
              <Tr key={k.id}>
                <Td className="font-mono text-xs">{k.keyPrefix}</Td>
                <Td>{keyStatusBadge(k)}</Td>
                <Td>{fmtDate(k.createdAt)}</Td>
                <Td right>
                  {k.status !== 'revoked' && (
                    <div className="flex justify-end gap-2">
                      {k.status === 'active' && (
                        <Button variant="secondary" size="sm" onClick={() => onRotate(k.id)}>
                          Rotate
                        </Button>
                      )}
                      <RevokeKeyButton
                        label="API key"
                        onConfirm={async () => {
                          await revoke({ slug, id: k.id }).unwrap()
                          toast.success('API key revoked')
                        }}
                      />
                    </div>
                  )}
                </Td>
              </Tr>
            ))}
          </tbody>
        )}
      </Table>
      <SecretRevealDialog secret={secret} title="API key generated" onClose={() => setSecret(null)} />
    </div>
  )
}

function RevokeKeyButton({ label, onConfirm }: { label: string; onConfirm: () => Promise<void> }) {
  const [open, setOpen] = useState(false)
  const [busy, setBusy] = useState(false)
  return (
    <AlertDialog open={open} onOpenChange={setOpen}>
      <Button variant="ghost" size="sm" className="text-ata-red hover:bg-ata-red/10" onClick={() => setOpen(true)}>
        Revoke
      </Button>
      <AlertDialogContent title={`Revoke this ${label}?`}>
        <AlertDialogDescription>
          <p>
            Requests signed with this {label} will fail <strong>immediately</strong>. This cannot be undone.
          </p>
        </AlertDialogDescription>
        <div className="mt-5 flex justify-end gap-2">
          <AlertDialogCancel asChild>
            <Button variant="secondary">Cancel</Button>
          </AlertDialogCancel>
          <AlertDialogAction asChild>
            <Button
              variant="destructive"
              loading={busy}
              onClick={async (e) => {
                e.preventDefault()
                setBusy(true)
                try {
                  await onConfirm()
                  setOpen(false)
                } catch (err) {
                  toast.error(apiError(err))
                } finally {
                  setBusy(false)
                }
              }}
            >
              Revoke {label}
            </Button>
          </AlertDialogAction>
        </div>
      </AlertDialogContent>
    </AlertDialog>
  )
}
